import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import Select from '@components/Select';
import { PaymentTypes } from '@constants/common';
import { ORDER_SERVICE_TYPE_LABELS } from '@constants/orderServiceType';
import { getFormattedDate } from '@utilities/common';
import {
  canMergeOnlineOrder,
  updateOnlineOrderDeliveryEta,
  updateOnlineOrderPayment,
  updateOnlineOrderStatus,
} from '@utilities/onlineOrders';
import { hasPermission } from '@utilities/common';
import { logError } from '@actions/userActions';
import type { AppDispatch } from '@app/store';
import { connect } from 'react-redux';
import type { OnlineOrder, OnlineOrderStatus } from '@src/types/onlineOrder';
import {
  DELIVERY_MINUTE_PRESETS,
  PAYMENT_SELECT_MENU_PROPS,
  STATUS_SELECT_MENU_PROPS,
  STATUS_SELECT_OPTIONS,
} from './constants';
import { getOrderStatusCardClass } from './OrderStatusBadge';
import OnlineOrderMergeDialog from './OnlineOrderMergeDialog';
import OnlineOrderPrintActions from './OnlineOrderPrintActions';

interface OnlineOrderCardProps {
  order: OnlineOrder;
  onUpdated: () => void;
  selectedRestaurant: any;
  userPermissions: any;
  cart: Record<string, Record<string, unknown>>;
  allTabs: Record<string, number>;
  selectedRestId: string;
  dispatch: AppDispatch;
}

function normalizeStatusForSelect(status: OnlineOrderStatus): OnlineOrderStatus {
  return status === 'ready' ? 'waiting_for_rider' : status;
}

function formatEtaDisplay(order: OnlineOrder): string | null {
  if (order.estimatedDeliveryAt) {
    return getFormattedDate(new Date(order.estimatedDeliveryAt));
  }
  if (order.estimatedDeliveryMinutes !== undefined && order.estimatedDeliveryMinutes !== null) {
    return `${order.estimatedDeliveryMinutes} min from acceptance`;
  }
  return null;
}

function OnlineOrderCard({
  order,
  onUpdated,
  selectedRestaurant,
  userPermissions,
  cart,
  allTabs,
  selectedRestId,
  dispatch,
}: OnlineOrderCardProps) {
  const canPrintKOT = hasPermission(userPermissions, 'printKOT', 'read');
  const [status, setStatus] = useState<OnlineOrderStatus>(normalizeStatusForSelect(order.status));
  const [paymentMode, setPaymentMode] = useState(order.selectedPayment ?? 'pending');
  const [deliveryMinutes, setDeliveryMinutes] = useState<string>(
    order.estimatedDeliveryMinutes !== undefined && order.estimatedDeliveryMinutes !== null
      ? String(order.estimatedDeliveryMinutes)
      : ''
  );
  const [deliveryAt, setDeliveryAt] = useState<string>(
    order.estimatedDeliveryAt ? order.estimatedDeliveryAt.slice(0, 16) : ''
  );
  const [saving, setSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [etaSaving, setEtaSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  useEffect(() => {
    setStatus(normalizeStatusForSelect(order.status));
    setPaymentMode(order.selectedPayment ?? 'pending');
    setDeliveryMinutes(
      order.estimatedDeliveryMinutes !== undefined && order.estimatedDeliveryMinutes !== null
        ? String(order.estimatedDeliveryMinutes)
        : ''
    );
    setDeliveryAt(order.estimatedDeliveryAt ? order.estimatedDeliveryAt.slice(0, 16) : '');
  }, [order]);

  const stopAccordionToggle = (event: React.MouseEvent | React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleStatusChange = async (next: { value: OnlineOrderStatus; label: string } | null) => {
    if (!next || next.value === normalizeStatusForSelect(order.status)) return;
    setSaving(true);
    try {
      const minutes = deliveryMinutes ? Number(deliveryMinutes) : undefined;
      const etaIso = deliveryAt ? new Date(deliveryAt).toISOString() : undefined;
      await updateOnlineOrderStatus({
        orderId: order.id,
        status: next.value,
        statusHistory: order.statusHistory,
        estimatedDeliveryMinutes: minutes,
        estimatedDeliveryAt: etaIso,
      });
      setStatus(next.value);
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentChange = async (next: { value: string; label: string } | null) => {
    if (!next?.value || next.value === paymentMode) return;
    setPaymentSaving(true);
    try {
      await updateOnlineOrderPayment(order.id, next.value);
      setPaymentMode(next.value);
      onUpdated();
    } catch (error) {
      dispatch(
        logError({
          severity: 'error',
          message: error instanceof Error ? error.message : 'Failed to update payment mode.',
        })
      );
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleSaveEta = async () => {
    setEtaSaving(true);
    try {
      const minutes = deliveryMinutes ? Number(deliveryMinutes) : undefined;
      const etaIso = deliveryAt ? new Date(deliveryAt).toISOString() : undefined;
      await updateOnlineOrderDeliveryEta(order.id, minutes, etaIso);
      onUpdated();
    } finally {
      setEtaSaving(false);
    }
  };

  const applyPreset = (minutes: number) => {
    setDeliveryMinutes(String(minutes));
    const eta = new Date(Date.now() + minutes * 60_000);
    const local = new Date(eta.getTime() - eta.getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 16);
    setDeliveryAt(local);
  };

  const etaDisplay = formatEtaDisplay(order);
  const orderLabel = order.orderSeqNo ?? order.orderCode;
  const customerLocation = order.placedAtCoords;
  const customerMapsUrl = customerLocation
    ? `https://www.google.com/maps?q=${customerLocation.latitude},${customerLocation.longitude}`
    : null;
  const showMerge = canMergeOnlineOrder(order);
  const paymentOption =
    PaymentTypes.find((option) => option.value === paymentMode) ??
    PaymentTypes.find((option) => option.value === 'pending') ??
    PaymentTypes[0];

  return (
    <>
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      className={`online-order-card ${getOrderStatusCardClass(status)}${expanded ? ' online-order-card--expanded' : ''}`}
      disableGutters
      elevation={0}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon className="online-order-card__chevron" />}>
        <div className="online-order-card__summary">
          <div className="online-order-card__summary-main">
            <div className="online-order-card__id-row">
              <Typography className="online-order-card__id" component="span">
                {orderLabel}
              </Typography>
              <div className="online-order-card__total">
                <span className="online-order-card__total-label">Total</span>
                <span className="online-order-card__total-value">₹{order.totalCost.toFixed(0)}</span>
              </div>
            </div>
            <div className="online-order-card__meta">
              <span className="online-order-card__meta-item">
                <ScheduleOutlinedIcon fontSize="inherit" />
                {getFormattedDate(new Date(order.placedAt))}
              </span>
              <span className="online-order-card__meta-item">
                <PlaceOutlinedIcon fontSize="inherit" />
                {order.tableOrRoom}
              </span>
              <span className="online-order-card__meta-item">
                <PhoneOutlinedIcon fontSize="inherit" />
                {order.phone}
              </span>
              <span className="online-order-card__meta-item">
                <ShoppingBagOutlinedIcon fontSize="inherit" />
                {order.itemCount} items
              </span>
              <span className="online-order-card__meta-item online-order-card__meta-item--payment">
                Pay: {paymentMode}
              </span>
              <span className="online-order-card__meta-item online-order-card__meta-item--service">
                {ORDER_SERVICE_TYPE_LABELS[order.serviceType]}
              </span>
              {customerMapsUrl && (
                <a
                  className="online-order-card__meta-item online-order-card__meta-item--link"
                  href={customerMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={stopAccordionToggle}
                >
                  <PlaceOutlinedIcon fontSize="inherit" />
                  Customer GPS
                </a>
              )}
            </div>
          </div>

          <div className="online-order-card__summary-actions">
            <div
              className="online-order-card__payment-wrap"
              onClick={stopAccordionToggle}
              onMouseDown={stopAccordionToggle}
              onKeyDown={stopAccordionToggle}
            >
              <Select
                className="online-order-payment-select online-order-payment-select--summary"
                value={paymentOption}
                options={PaymentTypes}
                isDisabled={paymentSaving}
                onChange={handlePaymentChange}
                onMouseDown={stopAccordionToggle}
                onTouchStart={stopAccordionToggle}
                {...PAYMENT_SELECT_MENU_PROPS}
              />
              {paymentSaving && <CircularProgress size={16} className="online-order-field__spinner" />}
            </div>
            <div
              className="online-order-card__status-wrap"
              onClick={stopAccordionToggle}
              onMouseDown={stopAccordionToggle}
              onKeyDown={stopAccordionToggle}
            >
              <Select
                className="online-order-status-select online-order-status-select--summary"
                value={STATUS_SELECT_OPTIONS.find((o) => o.value === status) ?? STATUS_SELECT_OPTIONS[0]}
                options={STATUS_SELECT_OPTIONS}
                isDisabled={saving}
                onChange={handleStatusChange}
                onMouseDown={stopAccordionToggle}
                onTouchStart={stopAccordionToggle}
                {...STATUS_SELECT_MENU_PROPS}
              />
              {saving && <CircularProgress size={16} className="online-order-field__spinner" />}
            </div>
            {showMerge && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                className="online-order-merge-btn"
                startIcon={<MergeTypeIcon />}
                onClick={(event) => {
                  stopAccordionToggle(event);
                  setMergeDialogOpen(true);
                }}
              >
                Merge
              </Button>
            )}
            <OnlineOrderPrintActions
              order={order}
              restaurant={selectedRestaurant}
              canPrintKOT={canPrintKOT}
              onClickCapture={stopAccordionToggle}
              compact
            />
          </div>
        </div>
      </AccordionSummary>

      <AccordionDetails className="online-order-card__details">
        <div className="online-order-card__panels">
          <section className="online-order-panel online-order-panel--items">
            <div className="online-order-panel__head">
              <Typography className="online-order-panel__title">Order items</Typography>
              <div className="online-order-panel__head-actions">
                <span className="online-order-panel__count">{order.cart.length} dishes</span>
                <OnlineOrderPrintActions
                  order={order}
                  restaurant={selectedRestaurant}
                  canPrintKOT={canPrintKOT}
                  className="online-order-print-actions--expanded-panel"
                />
              </div>
            </div>

            <ul className="online-order-item-list">
              {order.cart.map((line) => (
                <li key={line.id} className="online-order-item">
                  <div className="online-order-item__qty">{line.quantity}×</div>
                  <div className="online-order-item__body">
                    <span className="online-order-item__name">{line.itemName}</span>
                    {line.variantSize && (
                      <span className="online-order-item__variant">{line.variantSize}</span>
                    )}
                  </div>
                  <span className="online-order-item__price">₹{line.lineTotal.toFixed(0)}</span>
                </li>
              ))}
            </ul>

            {order.instructions && (
              <div className="online-order-note">
                <StickyNote2OutlinedIcon fontSize="small" />
                <span>{order.instructions}</span>
              </div>
            )}

            <div className="online-order-bill">
              <div className="online-order-bill__row">
                <span>Items</span>
                <span>₹{order.itemsCost.toFixed(0)}</span>
              </div>
              {order.gstBill > 0 && (
                <div className="online-order-bill__row">
                  <span>GST</span>
                  <span>₹{order.gstBill.toFixed(0)}</span>
                </div>
              )}
              {order.serviceBill > 0 && (
                <div className="online-order-bill__row">
                  <span>Service</span>
                  <span>₹{order.serviceBill.toFixed(0)}</span>
                </div>
              )}
              <div className="online-order-bill__row online-order-bill__row--total">
                <span>Grand total</span>
                <span>₹{order.totalCost.toFixed(0)}</span>
              </div>
            </div>
          </section>

          <section className="online-order-panel online-order-panel--actions">
            <div className="online-order-panel__head">
              <Typography className="online-order-panel__title">Delivery settings</Typography>
            </div>

            {etaDisplay && (
              <div className="online-order-eta-current">
                <ScheduleOutlinedIcon fontSize="small" />
                <span>Current ETA: {etaDisplay}</span>
              </div>
            )}

            <div className="online-order-field">
              <label className="online-order-field__label">Expected delivery</label>
              <div className="online-order-card__eta-presets">
                {DELIVERY_MINUTE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`online-order-eta-chip${
                      deliveryMinutes === String(preset.value) ? ' online-order-eta-chip--active' : ''
                    }`}
                    onClick={() => applyPreset(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="online-order-field-row">
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Minutes"
                value={deliveryMinutes}
                onChange={(e) => setDeliveryMinutes(e.target.value)}
                inputProps={{ min: 1, max: 240 }}
                className="online-order-input"
              />
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Date & time"
                value={deliveryAt}
                onChange={(e) => setDeliveryAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className="online-order-input"
              />
            </div>

            <Button
              variant="contained"
              fullWidth
              disabled={etaSaving}
              onClick={handleSaveEta}
              className="online-order-save-btn"
            >
              {etaSaving ? 'Saving…' : 'Save delivery time'}
            </Button>
          </section>
        </div>
      </AccordionDetails>
    </Accordion>

    <OnlineOrderMergeDialog
      open={mergeDialogOpen}
      sourceOrder={order}
      allTabs={allTabs}
      restaurantCart={cart}
      restaurantId={selectedRestId}
      dispatch={dispatch}
      onClose={() => setMergeDialogOpen(false)}
      onMerged={onUpdated}
      onSuccess={(message) => dispatch(logError({ severity: 'success', message }))}
      onError={(message) => dispatch(logError({ severity: 'error', message }))}
    />
    </>
  );
}

const mapStateToProps = (state: any) => {
  const { user, common, cart } = state;
  const selectedRestId = common?.selectedRestaurant?.docId || '';
  return {
    selectedRestaurant: common?.selectedRestaurant ?? null,
    cart: (cart && cart[selectedRestId]) || {},
    allTabs: common?.selectedRestaurant?.tabs || {},
    selectedRestId,
    userPermissions: user?.user?.roles
      ? {
          ...common?.globalConfig?.permissions,
          ...(user?.user?.roles[selectedRestId]?.permissions),
        }
      : {},
  };
};

export default connect(mapStateToProps)(OnlineOrderCard);
